import React, { useState, useEffect, useMemo, useContext, createContext } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { format, subDays, isWithinInterval, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import EmptyDataState from '../moodChart/EmptyDataState';
import CustomDot from '../moodChart/CustomDot';
import { CustomTooltip } from '../moodChart/CustomTooltip';
import MoodDistribution from '../moodChart/MoodDistribution';
import MoodStats from '../moodChart/MoodStats';
import PeriodFilter from '../moodChart/PeriodFilter';

// Definición de tipos para TypeScript
interface EntryMetrics {
  mood?: number;
  energy?: number;
  motivation?: number;
  accomplishment?: number;
  reflection?: number;
}

interface JournalEntry {
  _id: string;
  userId: string;
  date: string;
  type: 'morning' | 'evening';
  content: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  metrics?: EntryMetrics;
}

interface ChartDataPoint {
  dayKey: string;
  dayName: string;
  fullDate: string;
  mood: number | null;
  type: {
    morning: boolean;
    evening: boolean;
  };
  content: {
    morning?: Record<string, string>;
    evening?: Record<string, string>;
  };
  date: Date;
}

export const MoodConfigContext = createContext(null);
export const useMoodConfig = () => {
  const context = useContext(MoodConfigContext);
  if (!context) {
    throw new Error('useMoodConfig must be used within a MoodConfigProvider');
  }
  return context;
};

// Función para parsear el contenido JSON almacenado como string
const parseContent = (contentString: string): Record<string, string> => {
  try {
    return JSON.parse(contentString);
  } catch (error) {
    console.error("Error parsing content:", error);
    return {};
  }
};


// Componente que muestra la diferencia de estado de ánimo entre mañana y tarde
const MoodDifferenceIndicator = ({ morningMood, eveningMood }) => {
  const { t } = useTranslation();

  if (morningMood === null || eveningMood === null) return null;

  const difference = eveningMood - morningMood;
  let icon, color, text;

  if (difference > 0) {
    icon = '↗️';
    color = '#10B981'; // verde
    text = t('moodChart.improved');
  } else if (difference < 0) {
    icon = '↘️';
    color = '#EF4444'; // rojo
    text = t('moodChart.decreased');
  } else {
    icon = '→';
    color = '#6B7280'; // gris
    text = t('moodChart.noChange');
  }

  return (
    <div className="flex items-center space-x-1">
      <span>{icon}</span>
      <span className="text-xs" style={{ color }}>{text}</span>
    </div>
  );
};

export const getMoodConfig = () => {
  const { t } = useTranslation();

  return useMemo(() => ({
    1: {
      text: t('moodChart.levels.veryLow'),
      color: '#EF4444',
      emoji: '😢',
      description: t('moodChart.descriptions.veryLow'),
      tip: t('moodChart.tips.veryLow')
    },
    2: {
      text: t('moodChart.levels.low'),
      color: '#F59E0B',
      emoji: '😕',
      description: t('moodChart.descriptions.low'),
      tip: t('moodChart.tips.low')
    },
    3: {
      text: t('moodChart.levels.neutral'),
      color: '#10B981',
      emoji: '😐',
      description: t('moodChart.descriptions.neutral'),
      tip: t('moodChart.tips.neutral')
    },
    4: {
      text: t('moodChart.levels.good'),
      color: '#3B82F6',
      emoji: '😊',
      description: t('moodChart.descriptions.good'),
      tip: t('moodChart.tips.good')
    },
    5: {
      text: t('moodChart.levels.excellent'),
      color: '#6366F1',
      emoji: '😄',
      description: t('moodChart.descriptions.excellent'),
      tip: t('moodChart.tips.excellent')
    }
  }), [t]);
};

export const MoodChart = ({ entries = [] }: { entries: JournalEntry[] }) => {
  const { t, i18n } = useTranslation();
  const [period, setPeriod] = useState('week');
  const [filteredData, setFilteredData] = useState<ChartDataPoint[]>([]);
  const [showStats, setShowStats] = useState(true);
  const [showDistribution, setShowDistribution] = useState(false);

  const moodConfig = getMoodConfig();

  useEffect(() => {
    if (!entries.length) return;

    const now = new Date();
    let filterStart, filterEnd;

    switch (period) {
      case 'week':
        filterStart = startOfWeek(now, { weekStartsOn: 1 });
        filterEnd = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'month':
        filterStart = startOfMonth(now);
        filterEnd = endOfMonth(now);
        break;
      default: // 'all'
        filterStart = null;
        filterEnd = null;
    }

    const dateLocale = i18n.language.startsWith('es') ? es : undefined;

    const processedData = entries.reduce((acc, entry) => {
      const date = new Date(entry.date);

      if (filterStart && filterEnd && !isWithinInterval(date, { start: filterStart, end: filterEnd })) {
        return acc;
      }

      const dayKey = format(date, 'yyyy-MM-dd');

      if (!acc[dayKey]) {
        acc[dayKey] = {
          dayKey,
          dayName: format(date, 'EEE', { locale: dateLocale }),
          fullDate: format(date, 'EEEE, d MMMM', { locale: dateLocale }),
          morningMood: null,
          eveningMood: null,
          moodSum: 0,
          moodCount: 0,
          type: {
            morning: false,
            evening: false
          },
          content: {
            morning: {},
            evening: {}
          },
          date
        };
      }

      // Procesar tipo específico (mañana o noche)
      if (entry.type === 'morning' && entry.metrics?.mood) {
        acc[dayKey].morningMood = entry.metrics.mood;
        acc[dayKey].moodSum += entry.metrics.mood;
        acc[dayKey].moodCount++;
        acc[dayKey].type.morning = true;
        acc[dayKey].content.morning = parseContent(entry.content);
      } else if (entry.type === 'evening' && entry.metrics?.mood) {
        acc[dayKey].eveningMood = entry.metrics.mood;
        acc[dayKey].moodSum += entry.metrics.mood;
        acc[dayKey].moodCount++;
        acc[dayKey].type.evening = true;
        acc[dayKey].content.evening = parseContent(entry.content);
      }

      return acc;
    }, {});

    // Convertir a formato de gráfico y ordenar por fecha
    const chartData = Object.values(processedData).map(day => ({
      dayKey: day.dayKey,
      dayName: day.dayName,
      fullDate: day.fullDate,
      mood: day.moodCount > 0 ? Math.round(day.moodSum / day.moodCount) : null,
      morningMood: day.morningMood,
      eveningMood: day.eveningMood,
      type: day.type,
      content: day.content,
      date: day.date
    })).sort((a, b) => a.date.getTime() - b.date.getTime());

    setFilteredData(chartData);
  }, [entries, period, i18n.language]);

  // Calcular promedio para línea de referencia
  const validMoods = filteredData.filter(entry => entry.mood !== null);
  const averageMood = validMoods.length > 0
    ? validMoods.reduce((sum, entry) => sum + entry.mood, 0) / validMoods.length
    : null;

  return (
    <MoodConfigContext.Provider value={moodConfig}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-2xl mx-auto space-y-4"
      >
        {/* Header con título y filtros */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-gray-200">
            {t('moodChart.title')}
          </h3>
          <div className="flex items-center space-x-2">
            <PeriodFilter activePeriod={period} onChange={setPeriod} />

            {/* Botón para estadísticas */}
            <button
              onClick={() => setShowStats(!showStats)}
              className={`p-1.5 sm:p-2 rounded-lg border transition-colors ${showStats
                ? 'bg-indigo-100 border-indigo-300 text-indigo-600 dark:bg-indigo-900 dark:border-indigo-700 dark:text-indigo-300'
                : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              title={t('moodChart.toggleStats')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </button>

            {/* Botón para distribución */}
            <button
              onClick={() => setShowDistribution(!showDistribution)}
              className={`p-1.5 sm:p-2 rounded-lg border transition-colors ${showDistribution
                ? 'bg-indigo-100 border-indigo-300 text-indigo-600 dark:bg-indigo-900 dark:border-indigo-700 dark:text-indigo-300'
                : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              title={t('moodChart.toggleDistribution')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Panel de estadísticas */}
        <AnimatePresence>
          {showStats && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <MoodStats chartData={filteredData} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Panel de distribución */}
        <AnimatePresence>
          {showDistribution && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <MoodDistribution chartData={filteredData} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Gráfico */}
        <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          {filteredData.length > 0 ? (
            <div className="h-52 sm:h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={filteredData}
                  margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#E5E7EB"
                    opacity={0.5}
                  />
                  <XAxis
                    dataKey="dayName"
                    tick={{ fontSize: 10, fill: '#6B7280' }}
                    tickLine={false}
                    axisLine={false}
                    padding={{ left: 10, right: 10 }}
                  />
                  <YAxis
                    domain={[0, 5]}
                    ticks={[1, 2, 3, 4, 5]}
                    tick={{ fontSize: 10, fill: '#6B7280' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => value > 0 ? moodConfig[value]?.emoji || value : ''}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ stroke: '#E5E7EB' }}
                  />
                  {averageMood && (
                    <ReferenceLine
                      y={averageMood}
                      stroke="#9CA3AF"
                      strokeDasharray="3 3"
                      label={{
                        value: t('moodChart.average'),
                        position: 'right',
                        fill: '#6B7280',
                        fontSize: 10
                      }}
                    />
                  )}
                  <defs>
                    <linearGradient id="moodGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366F1" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Line
                    type="monotone"
                    dataKey="mood"
                    stroke="#6366F1"
                    strokeWidth={2.5}
                    dot={(props) => <CustomDot {...props} />}
                    activeDot={{ r: 8, fill: "#6366F1" }}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyDataState period={period} />
          )}
        </div>

        {/* Nota informativa */}
        <p className="text-xs text-center text-gray-500 dark:text-gray-400 italic">
          {t('moodChart.clickForDetails')}
        </p>
      </motion.div>
    </MoodConfigContext.Provider>
  );
};

export default MoodChart;